import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { AddressManager } from "@/components/AddressManager";
import { ArrowLeft, ShieldCheck, Phone as PhoneIcon, Calendar, IndianRupee, MapPin } from "lucide-react";

const STATUS_COLOR = {
  awaiting: "bg-hyd-gold/15 text-hyd-gold",
  confirmed: "bg-blue-500/15 text-blue-300",
  packed: "bg-blue-500/15 text-blue-300",
  dispatched: "bg-indigo-500/15 text-indigo-300",
  delivered: "bg-emerald-500/15 text-emerald-300",
  cancelled: "bg-red-500/15 text-red-300",
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [userId]);

  if (data === null) return <div className="text-hyd-text2">Loading...</div>;
  if (data === false) return <div className="text-hyd-text2">User not found.</div>;

  const { user, orders, orders_count, total_spent } = data;
  const avg = orders_count ? total_spent / orders_count : 0;

  return (
    <div>
      <Link to="/admin/users" className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to users
      </Link>

      <div className="mt-3 bg-hyd-panel border border-hyd-border rounded-3xl p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-hyd-gold text-hyd-bg grid place-items-center font-display text-2xl font-bold">
          {user.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-3xl font-extrabold">{user.name}</h1>
          <div className="text-hyd-text2 text-sm flex items-center gap-3 flex-wrap mt-1">
            <span className="flex items-center gap-1"><PhoneIcon className="w-3.5 h-3.5" /> {user.phone}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Joined {new Date(user.created_at).toLocaleDateString()}</span>
            {user.role === "admin" && <span className="flex items-center gap-1 text-hyd-gold"><ShieldCheck className="w-3.5 h-3.5" /> Admin</span>}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Orders" value={orders_count} />
        <Stat label="Lifetime spend" value={inr(total_spent)} icon={IndianRupee} />
        <Stat label="Avg. order" value={inr(avg)} />
      </div>

      <h2 className="font-display text-xl font-bold mt-6 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-hyd-gold" /> Addresses
      </h2>
      <div className="mt-3 bg-hyd-panel border border-hyd-border rounded-3xl p-4">
        <AddressManager endpoint={`/admin/users/${userId}/addresses`} showTitle={false} />
      </div>

      <h2 className="font-display text-xl font-bold mt-6">Orders</h2>
      {orders.length === 0 ? (
        <div className="mt-3 bg-hyd-panel border border-hyd-border rounded-3xl p-10 text-center text-hyd-text3">No orders yet.</div>
      ) : (
        <div className="mt-3 bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Order #</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Items</th>
                <th className="text-right p-3">Total</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Ship to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hyd-border">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-hyd-panel2/60">
                  <td className="p-3 font-mono text-xs text-hyd-text2">{o.order_number}</td>
                  <td className="p-3 text-xs text-hyd-text3">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-3 text-right">{o.items.length}</td>
                  <td className="p-3 text-right text-hyd-gold font-bold">{inr(o.total)}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || "bg-hyd-panel text-hyd-text2"}`}>{o.status}</span></td>
                  <td className="p-3 text-xs text-hyd-text2">{o.address.city}, {o.address.pincode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-4">
      <div className="text-[10px] uppercase text-hyd-text2 tracking-widest flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-hyd-gold" />} {label}
      </div>
      <div className="font-display text-2xl font-bold text-hyd-gold mt-1">{value}</div>
    </div>
  );
}
